import { Button, CryptoIcon, Badge } from './ui'
import { Card, CardContent } from './ui/card'
import { cn } from '../lib/utils'
import { Copy } from 'lucide-react'
import { toast } from 'sonner'

export interface AddressCardProps {
  address: {
    id: string
    address: string
    label?: string | null
    chain: string
    status: string
    decisionBy?: string | null
    requester?: {
      id: string
      email: string
      username: string
    } | null
  }
  onApprove?: (id: string) => void
  onReject?: (id: string) => void
  onManage?: (address: any) => void
  showApproveButton?: boolean
  showRejectButton?: boolean
  showManageButton?: boolean
  className?: string
}

export function AddressCard({
  address,
  onApprove,
  onReject,
  onManage,
  showApproveButton = false,
  showRejectButton = false,
  showManageButton = false,
  className,
}: AddressCardProps) {
  const handleCopyAddress = (addressText: string) => {
    navigator.clipboard.writeText(addressText)
    toast.success('Address copied to clipboard')
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-main-green-muted text-main-green border border-main-green'
      case 'REQUESTED':
        return 'bg-main-orange-muted text-main-orange border border-main-orange'
      default:
        return 'bg-main-green-muted text-main-green border border-main-green'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'REQUESTED':
        return 'Requested'
      case 'APPROVED':
        return 'Approved'
      default:
        return status
    }
  }

  return (
    <Card className={cn('relative', className)}>
      <CardContent className="px-4">
        {/* Header with name and status */}
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-bold text-lg text-gray-900 truncate">
            {address.label || 'Untitled'}
          </h3>
          <Badge
            variant="main_outline"
            className={cn(getStatusColor(address.status), 'text-xs px-2 py-1 leading-none h-auto')}
          >
            {getStatusText(address.status)}
          </Badge>
        </div>

        {/* Chain info */}
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-full flex items-center justify-center">
            <CryptoIcon symbol={address.chain || ''} size="h-8 w-8" />
          </div>
          <span className="text-sm text-gray-600">{address.chain}</span>
        </div>

        {/* Address section */}
        <div className="space-y-2 pt-4">
          <div className="flex items-center gap-2">
            <div className={cn('text-xs text-card-text rounded-md w-fit')}>Address</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-600">{address.address}</span>

            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'text-xs text-card-text border border-card-border rounded-md px-2 py-1 w-fit cursor-pointer',
                'h-auto md:h-auto rounded-md gap-1.5 px-2 py-1.5 has-[>svg]:px-2.5',
              )}
              onClick={() => handleCopyAddress(address.address)}
            >
              <Copy className="size-3" />
              <span className="text-xs leading-none">Copy ID</span>
            </Button>
          </div>
        </div>

        {/* Requester information */}
        {address.requester && (
          <div className="space-y-2 pt-4">
            <div className="flex items-center gap-2">
              <div className={cn('text-xs text-card-text rounded-md w-fit')}>Requested by</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{address.requester.username}</span>
            </div>
          </div>
        )}

        {address.decisionBy && (
          <div className="space-y-2 pt-4">
            <div className="flex items-center gap-2">
              <div className="text-xs text-card-text rounded-md w-fit">Decision by</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{address.decisionBy}</span>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100">
          {showApproveButton && onApprove && (
            <Button
              variant="main_action"
              size="sm"
              className="text-xs flex-1 rounded-md py-2 md:py-0"
              onClick={() => onApprove(address.id)}
            >
              Approve
            </Button>
          )}
          {showRejectButton && onReject && (
            <Button
              variant="main_outline"
              size="sm"
              className="text-xs flex-1 rounded-md py-2 md:py-0"
              onClick={() => onReject(address.id)}
            >
              Reject
            </Button>
          )}
          {showManageButton && onManage && (
            <Button
              variant="main_action"
              size="sm"
              className="text-xs flex-1 rounded-md py-2 md:py-0"
              onClick={() => onManage(address)}
            >
              Manage
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
